import dayjs from 'dayjs';
import { formatMoney } from './currency';

export function formatDate(value?: string | null, fmt = 'DD MMM YYYY'): string {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format(fmt) : '—';
}

export function formatDateTime(value?: string | null): string {
  return formatDate(value, 'DD MMM YYYY, hh:mm A');
}

export function fromNow(value?: string | null): string {
  if (!value) return '—';
  const d = dayjs(value);
  if (!d.isValid()) return '—';
  const diff = d.diff(dayjs(), 'day');
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return d.format('DD MMM YYYY');
}

/**
 * Legacy currency formatter — kept for backward compatibility with existing pages (default en-IN, whole rupees).
 * For locale/currency-aware, tenant-driven formatting prefer the `useCurrency().format` hook (Phase 14.3), which
 * this now delegates to under the hood.
 */
export function formatCurrency(value?: number | null, currency = 'INR'): string {
  if (value == null) return '—';
  return formatMoney(value, { currency, locale: 'en-IN', precision: 0 });
}

export function formatNumber(value?: number | null): string {
  if (value == null) return '—';
  return value.toLocaleString();
}

/** Convert a .NET/Unicode date pattern to a dayjs pattern (best-effort). */
export function netPatternToDayjs(fmt: string): string {
  return fmt.replace(/yyyy/g, 'YYYY').replace(/yy/g, 'YY').replace(/dd/g, 'DD').replace(/tt/g, 'A');
}

/** Format a datetime with a backend .NET-style pattern (Phase 14.10). "o" → ISO-8601. */
export function formatWithPattern(value: string | number | Date, netPattern: string): string {
  const d = dayjs(value);
  if (!d.isValid()) return '—';
  if (netPattern === 'o' || netPattern === 'O') return d.toISOString();
  return d.format(netPatternToDayjs(netPattern));
}

export function formatBytes(bytes?: number | null): string {
  if (bytes == null) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function initials(name?: string | null): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

// Deterministic pastel color from a string (avatars, chips).
export function colorFromString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 55%)`;
}

